import { createContext } from 'react';

import type { CanvasScope, CanvasUploadDiagnostic, NodePatchPackage } from '@/types/canvas';
import type { CanvasUploadResult } from '@/types/graph';

import type { UploadBatch, UploadMode, UploadSourceKind } from './types';

export interface StartBatchOptions {
  projectKey: string;
  projectName?: string;
  assetType: number;
  assetTypeLabel?: string;
  mode: UploadMode;
  source: UploadSourceKind;
  files: File[];
  /** 请求发起时冻结的 Canvas URL Scope。 */
  scope?: CanvasScope;
  /** 上传接口返回节点后、批次标记成功前执行的 Canvas 落位。 */
  onUploaded?: (results: CanvasUploadResult[]) => Promise<void>;
  /** Canvas 统一上传接口返回的原生节点 Patch。 */
  onNodePatch?: (nodePatch: NodePatchPackage) => Promise<void>;
  /** 文件级拒绝/失败诊断，不参与 Canvas 状态归并。 */
  onDiagnostics?: (diagnostics: CanvasUploadDiagnostic[]) => void;
  /** 上传成功后回调，用于触发列表刷新等业务副作用 */
  onSuccess?: (result: { successCount: number; batchId: string }) => void;
  /** 上传失败回调（含取消） */
  onError?: (error: Error, batchId: string) => void;
}

export interface UploadCenterContextValue {
  /** 全部活跃 + 历史批次（已结束的会持久化） */
  batches: UploadBatch[];
  /** 启动一个批次，返回批次 ID（同步分配） */
  startBatch: (options: StartBatchOptions) => string;
  /** 取消正在上传的批次（终止 xhr） */
  cancelBatch: (batchId: string) => void;
  /** 删除单条历史 */
  removeBatch: (batchId: string) => void;
  /** 清空所有已完成（success/failed/cancelled）的批次 */
  clearFinished: () => void;
  /** 当前是否有正在上传的批次（用于浮窗自动展开） */
  hasUploading: boolean;
  /** 是否抑制全局浮窗（当内嵌进度组件可见时设为 true） */
  dockSuppressed: boolean;
  /** 控制全局浮窗的抑制状态 */
  setSuppressDock: (suppressed: boolean) => void;
  /** 注册"打开上传进度界面"的回调（由业务层如 ProjectWorkspace 调用），handler 可接收 assetTabKey 用于定位到对应 Tab */
  registerOpenProgressHandler: (handler: (assetTabKey?: string) => void) => void;
  /** 注销回调 */
  unregisterOpenProgressHandler: () => void;
  /** 各 Tab 最近上传成功的文件名列表（key = assetFolderKey，如 character/scene/prop/audio） */
  recentUploadNamesMap: Record<string, string[]>;
  /** 添加新上传成功的文件名（由上传成功回调调用） */
  addRecentUploadNames: (tabKey: string, names: string[]) => void;
  /** 清除指定 Tab 下的某个文件名红点（用户点击卡片时调用） */
  removeRecentUploadName: (tabKey: string, name: string) => void;
  /** 清除指定多个 Tab 的红点数据（弹窗关闭时，只清除用户已浏览过的 Tab） */
  clearRecentUploadNamesForTabs: (tabKeys: string[]) => void;
  /** 清除所有红点数据 */
  clearAllRecentUploadNames: () => void;
}

export const UploadCenterContext = createContext<UploadCenterContextValue | null>(null);
